import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  Clock,
  CheckCircle2,
  XCircle,
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  Trophy,
  RotateCcw
} from "lucide-react";

const QuizPage = ({ quizType }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [quizStarted, setQuizStarted] = useState(false);

  useEffect(() => {
    fetchQuiz();
  }, [id, quizType]);

  useEffect(() => {
    if (quizStarted && timeLeft !== null && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [quizStarted, timeLeft]);

  const fetchQuiz = async () => {
    try {
      const quizzesRes = await axios.get(`${API}/quizzes?course_id=${id}`);
      const targetQuiz = quizzesRes.data.find(q => q.quiz_type === quizType);
      
      if (!targetQuiz) {
        toast.error("Cuestionario no encontrado");
        navigate(`/courses/${id}`);
        return;
      }

      const quizDetail = await axios.get(`${API}/quizzes/${targetQuiz.id}`);
      setQuiz(quizDetail.data);
      
      // Parse JSON fields from PostgreSQL JSONB
      let questionsData = (quizDetail.data.questions || []).map(q => ({
        ...q,
        options: typeof q.options === 'string' ? JSON.parse(q.options) : q.options,
        correct_answers: typeof q.correct_answers === 'string' ? JSON.parse(q.correct_answers) : q.correct_answers
      }));
      
      if (quizDetail.data.randomize) {
        questionsData = [...questionsData].sort(() => Math.random() - 0.5);
      }
      setQuestions(questionsData);
      setTimeLeft(quizDetail.data.time_limit * 60);
    } catch (error) {
      toast.error("Error al cargar el cuestionario");
      navigate(`/courses/${id}`);
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuiz = () => {
    setQuizStarted(true);
  };

  const handleSelectOption = (questionId, optionIndex) => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return;

    setAnswers(prev => {
      const currentAnswers = prev[questionId] || [];
      
      if (question.question_type === 'single' || question.question_type === 'true_false') {
        return { ...prev, [questionId]: [optionIndex] };
      } else {
        // Multiple selection
        if (currentAnswers.includes(optionIndex)) {
          return { ...prev, [questionId]: currentAnswers.filter(i => i !== optionIndex) };
        } else {
          return { ...prev, [questionId]: [...currentAnswers, optionIndex] };
        }
      }
    });
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/attempts`, {
        quiz_id: quiz.id,
        answers
      });
      setResult(response.data);
      toast.success("Cuestionario enviado correctamente");
    } catch (error) {
      const message = error.response?.data?.detail || "Error al enviar el cuestionario";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerClass = () => {
    if (timeLeft <= 60) return 'timer-danger';
    if (timeLeft <= 300) return 'timer-warning';
    return '';
  };

  const currentQuestion = questions[currentIndex];
  const answeredCount = Object.keys(answers).length;
  const progressPercent = (answeredCount / questions.length) * 100;

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="h-32 skeleton rounded-xl" />
        <div className="h-96 skeleton rounded-xl" />
      </div>
    );
  }

  // Show results
  if (result) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="quiz-results">
        <Button variant="ghost" onClick={() => navigate(`/courses/${id}`)} data-testid="back-to-course-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al curso
        </Button>

        <Card>
          <CardContent className="pt-8 pb-8 text-center">
            <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 ${
              result.passed ? 'bg-green-100' : 'bg-red-100'
            }`}>
              {result.passed ? (
                <Trophy className="w-10 h-10 text-green-600" />
              ) : (
                <XCircle className="w-10 h-10 text-red-600" />
              )}
            </div>
            
            <h2 className="text-2xl font-bold font-heading mb-2">
              {result.passed ? '¡Felicitaciones!' : 'Sigue intentando'}
            </h2>
            <p className="text-slate-600 mb-6">
              {result.passed 
                ? 'Has aprobado el cuestionario exitosamente.'
                : 'No alcanzaste la nota mínima requerida.'}
            </p>

            <div className="inline-flex items-center justify-center w-32 h-32 rounded-full border-8 border-slate-100 mb-6">
              <div>
                <p className={`text-4xl font-bold ${result.passed ? 'text-green-600' : 'text-red-600'}`}>
                  {result.score.toFixed(0)}%
                </p>
                <p className="text-sm text-slate-500">Puntuación</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 max-w-xs mx-auto mb-8">
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-slate-900">{questions.length}</p>
                <p className="text-sm text-slate-500">Preguntas</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-slate-900">{quiz.passing_score}%</p>
                <p className="text-sm text-slate-500">Nota mínima</p>
              </div>
            </div>

            <Button onClick={() => navigate(`/courses/${id}`)} data-testid="continue-btn">
              Continuar al curso
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Pre-quiz screen
  if (!quizStarted) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="quiz-intro">
        <Button variant="ghost" onClick={() => navigate(`/courses/${id}`)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al curso
        </Button>

        <Card>
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-heading">{quiz.title}</CardTitle>
            <p className="text-slate-500">
              {quizType === 'pretest' ? 'Evaluación inicial' : 'Evaluación final'}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-slate-900">{questions.length}</p>
                <p className="text-sm text-slate-500">Preguntas</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-slate-900">{quiz.time_limit}</p>
                <p className="text-sm text-slate-500">Minutos</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-slate-900">{quiz.passing_score}%</p>
                <p className="text-sm text-slate-500">Nota mínima</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-slate-900">{quiz.max_attempts}</p>
                <p className="text-sm text-slate-500">Intentos</p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800 mb-1">Instrucciones importantes</h4>
                  <ul className="text-sm text-amber-700 space-y-1">
                    {quiz.time_limit > 0 && (
                      <>
                        <li>• Una vez iniciado, el tiempo comenzará a correr.</li>
                        <li>• No podrás pausar el cuestionario.</li>
                      </>
                    )}
                    <li>• Asegúrate de tener una conexión estable.</li>
                    <li>• Revisa todas tus respuestas antes de enviar.</li>
                    {quiz.randomize && <li>• Las preguntas aparecerán en orden aleatorio.</li>}
                  </ul>
                </div>
              </div>
            </div>

            <Button onClick={handleStartQuiz} className="w-full h-12 text-lg" data-testid="start-quiz-btn">
              Comenzar {quizType === 'pretest' ? 'Pre-test' : 'Post-test'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Quiz in progress
  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="quiz-in-progress">
      {/* Timer */}
      <div className={`quiz-timer ${getTimerClass()}`}>
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5" />
          <span className={`timer-display ${getTimerClass()}`}>{formatTime(timeLeft)}</span>
        </div>
      </div>

      {/* Progress */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">
              Pregunta {currentIndex + 1} de {questions.length}
            </span>
            <span className="text-sm text-slate-600">
              {answeredCount} respondidas
            </span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </CardContent>
      </Card>

      {/* Question */}
      <Card>
        <CardContent className="py-8">
          <div className="mb-6">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
              currentQuestion.question_type === 'single' ? 'bg-blue-100 text-blue-800' :
              currentQuestion.question_type === 'multiple' ? 'bg-purple-100 text-purple-800' :
              'bg-green-100 text-green-800'
            }`}>
              {currentQuestion.question_type === 'single' ? 'Selección única' :
               currentQuestion.question_type === 'multiple' ? 'Selección múltiple' :
               'Verdadero / Falso'}
            </span>
          </div>

          <h3 className="text-xl font-medium text-slate-900 mb-6">
            {currentQuestion.text}
          </h3>

          <div className="space-y-3">
            {currentQuestion.options.map((option, optIndex) => {
              // Solo mostrar opciones con contenido
              if (!option || option.trim() === '') return null;
              
              const isSelected = (answers[currentQuestion.id] || []).includes(optIndex);
              return (
                <button
                  key={optIndex}
                  onClick={() => handleSelectOption(currentQuestion.id, optIndex)}
                  className={`quiz-option w-full text-left ${isSelected ? 'selected' : ''}`}
                  data-testid={`option-${optIndex}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'border-primary bg-primary' : 'border-slate-300'
                    }`}>
                      {isSelected && <CheckCircle2 className="w-4 h-4 text-white" />}
                    </div>
                    <span className="text-slate-700">{option}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
          disabled={currentIndex === 0}
          data-testid="prev-question-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Anterior
        </Button>

        <div className="flex gap-2">
          {questions.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                idx === currentIndex
                  ? 'bg-primary text-white'
                  : answers[questions[idx]?.id]
                  ? 'bg-green-100 text-green-700'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`question-nav-${idx}`}
            >
              {idx + 1}
            </button>
          ))}
        </div>

        {currentIndex < questions.length - 1 ? (
          <Button
            onClick={() => setCurrentIndex(prev => prev + 1)}
            data-testid="next-question-btn"
          >
            Siguiente
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={submitting || answeredCount < questions.length}
            className="bg-green-600 hover:bg-green-700"
            data-testid="submit-quiz-btn"
          >
            {submitting ? 'Enviando...' : 'Finalizar'}
          </Button>
        )}
      </div>

      {answeredCount < questions.length && currentIndex === questions.length - 1 && (
        <p className="text-center text-amber-600 text-sm">
          Responde todas las preguntas antes de enviar ({questions.length - answeredCount} pendientes)
        </p>
      )}
    </div>
  );
};

export default QuizPage;
